package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.controller.SettingsUiState
import com.dotdrives.design.DotTone
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotAvatar
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import com.dotdrives.web.ui.WorkflowFeedback
import com.dotdrives.web.ui.useDialogFocus
import com.dotdrives.controller.WorkflowController
import com.dotdrives.controller.WorkflowState
import kotlinx.browser.document
import react.useEffect
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.details
import react.dom.html.ReactHTML.summary
import web.cssom.ClassName
import web.dom.ElementId
import web.html.ButtonType
import web.html.button as buttonType

external interface UsersRolesPanelProps : Props {
    var controller: SettingsController
    var state: SettingsUiState
    var canManageUsers: Boolean
    var currentUserRole: String
    var workflow: WorkflowController
    var workflowState: WorkflowState
}

val UsersRolesPanel =
    FC<UsersRolesPanelProps> { props ->
        val state = props.state
        val selected = state.members.firstOrNull { it.userId == state.selectedMemberUserId }
        val showGoals = currentStewardBudgets(state.budgets).isNotEmpty()
        useDialogFocus(selected != null, "settings-member-detail", onClose = props.controller::closeMember)

        WorkflowFeedback { controller = props.workflow; this.state = props.workflowState }
        state.error?.let { error ->
            DotBanner {
                message = error
                onRetry = props.controller::retry
                className = "settings-inline-banner"
            }
        }

        div {
            className = ClassName("settings-users-grid" + if (showGoals) " settings-users-with-goals" else "")

            div {
                when {
                    state.loading && state.members.isEmpty() ->
                        div {
                            className = ClassName("settings-user-list")
                            repeat(5) { DotSkeleton { height = "58px" } }
                        }
                    state.members.isEmpty() && state.error == null ->
                        DotEmptyState {
                            emoji = "👥"
                            title = "No teammates yet"
                            body =
                                "Invite the people who'll manage drives, givers, and donations with you."

                        }
                    // Members failed to load: the banner above already carries the retry affordance
                    // -- an empty table shell underneath it would misread as "no teammates" rather
                    // than "couldn't load."
                    state.members.isEmpty() && state.error != null -> Unit
                    else -> {
                        div {
                            className = ClassName("settings-user-list-head")
                            div { +"USER" }
                            div { +"ROLE" }
                            if (showGoals) div { +"GOAL" }
                        }
                        div {
                            className = ClassName("settings-user-list")
                            state.members.forEach { member ->
                                val stewardBudget =
                                    currentStewardBudgets(state.budgets)[member.userId]
                                MemberRow {
                                    key = member.userId.unsafeCast<Key>()
                                    this.member = member
                                    goal = stewardBudget?.amount
                                    this.showGoals = showGoals
                                    this.selected = member.userId == state.selectedMemberUserId
                                    onSelect = { props.controller.selectMember(member.userId) }
                                }
                            }
                        }
                    }
                }
            }

        }
        if (selected != null) div {
            className = ClassName("settings-dialog-backdrop")
            onClick = { props.controller.closeMember() }
            div {
                id = "settings-member-detail".unsafeCast<ElementId>(); tabIndex = -1
                role = "dialog".unsafeCast<react.dom.aria.AriaRole>(); ariaModal = true; ariaLabel = "Edit ${selected.displayName}"
                className = ClassName("settings-dialog settings-member-sheet")
                onClick = { it.stopPropagation() }
                button { type = ButtonType.buttonType; className = ClassName("settings-sheet-close"); ariaLabel = "Close member details"; onClick = { props.controller.closeMember() }; +"×" }
                    MemberAccessPanel {
                        member = selected
                        members = state.members
                        role = props.currentUserRole
                        workflow = props.workflow
                        workflowState = props.workflowState
                    }
                    RolePanel { key = selected.userId.unsafeCast<Key>(); role = selected.role }
            }
        }
    }

private external interface MemberRowProps : Props {
    var member: OrgMember
    var goal: Double?
    var showGoals: Boolean
    var selected: Boolean
    var onSelect: () -> Unit
}

private val MemberRow =
    FC<MemberRowProps> { props ->
        val member = props.member
        button {
            type = ButtonType.buttonType
            className =
                ClassName(
                    "settings-user-row" + if (props.selected) " settings-user-row-selected" else ""
                )
            asDynamic()["aria-pressed"] = props.selected
            onClick = { props.onSelect() }
            div {
                className = ClassName("settings-user-identity")
                DotAvatar {
                    initials = memberInitials(member.displayName)
                    size = 34
                }
                div {
                    className = ClassName("settings-user-name")
                    +member.displayName
                }
            }
            div {
                className = ClassName("settings-user-role-cell")
                DotPill {
                    text =
                        if (member.deactivatedAt != null) "Deactivated" else roleLabel(member.role)
                    tone =
                        if (member.deactivatedAt != null) DotTone.Neutral else roleTone(member.role)
                }
            }
            if (props.showGoals) div {
                className =
                    ClassName(
                        "settings-user-goal" +
                            if (props.goal == null) " settings-user-goal-empty" else ""
                    )
                +formatMoneyCompact(props.goal)
            }

        }
    }

private external interface RolePanelProps : Props {
    var role: String
}

private val RolePanel =
    FC<RolePanelProps> { props ->
        details {
            className = ClassName("settings-permissions-summary")
            summary { +"Permissions summary" }
            div {
                className = ClassName("settings-section-subtitle")
                +roleDescription(props.role)
            }
            div {
                permissionsForRole(props.role).forEach { perm ->
                    div {
                        key = perm.name.unsafeCast<Key>()
                        className = ClassName("settings-perm-row")
                        span {
                            className = ClassName("settings-perm-name")
                            +perm.name
                        }
                        div {
                            className = ClassName("settings-perm-pills")
                            span {
                                className =
                                    ClassName(
                                        "settings-perm-pill" +
                                            if (perm.canRead) " settings-perm-pill-on" else ""
                                    )
                                +"READ"
                            }
                            span {
                                className =
                                    ClassName(
                                        "settings-perm-pill" +
                                            if (perm.canWrite) " settings-perm-pill-on" else ""
                                    )
                                +"WRITE"
                            }
                        }
                    }
                }
            }
        }
    }
